import { FC, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { isDebtorType1 } from 'reducers/activecase/activecaseSlice'
import { useMutation } from '@apollo/client'
import { useHistory } from 'react-router-dom'

import { BACKEND_URL } from 'AppSettings'
import { useAppDispatch, useAppSelector } from 'hooks/store'
import useLoadingDelay from 'hooks/useLoadingDelay'
import { InfoField } from 'types/infoField'
import { loadingSpinnerPortal } from 'components/atoms/LoadingSpinner'
import {
  getActiveDiscInstUrlInfo,
  setCourseDiscInstUrlInfoFields,
  setCourseLoading,
  setErrorMessage,
  setTitle
} from 'reducers/activecourse/activecourseSlice'
import {
  CourseMethodType,
  CourseOrderItem,
  CourseStatus,
  CourseTabTitle,
  getCourseStatusName,
  UpdateCourseDiscInstUrlInfoWithRegistration
} from 'share/domain/course'
import {
  shouldNavigateToVerifyPrimaryInformation,
  shouldNavigateToVerifySpouseInformation,
  shouldNavigateToDisclosureAgreement,
  shouldNavigateToCourseInstructions
} from 'share/utils/courseUtils'
import {
  REGISTER_COURSE,
  RegisterCourseVariables,
  RegisterCourseResponse,
  REGISTER_PHONE_COURSE,
  RegisterPhoneCourseVariables,
  RegisterPhoneCourseResponse,
  INCREMENT_ORDER_REGISTER_COURSE_COUNT,
  IncrementOrderRegisterCourseCountVariables,
  IncrementOrderRegisterCourseCountResponse
} from 'share/mutations/courses'
import { CoursesContainer } from 'components/atoms/Layout'
import InfoFields from 'components/atoms/InfoFields'

import { StartBtn } from './styles'

interface CourseTabStartProps {
  course: CourseOrderItem
  currentTab: string
}

const CourseTabStart: FC<CourseTabStartProps> = ({ course, currentTab }) => {
  const isDebtor1 = useSelector(isDebtorType1)
  const history = useHistory()
  const dispatch = useAppDispatch()
  const activeCase = useAppSelector((state) => state.activeCase)
  const activeCourse = useAppSelector((state) => state.activeCourse)
  const discInstUrlInfo = useAppSelector(getActiveDiscInstUrlInfo)

  const [registerCourse, { loading: registerLoading }] =
    useMutation<RegisterCourseResponse, RegisterCourseVariables>(
      REGISTER_COURSE
    )

  const [registerPhoneCourse, { loading: registerPhoneLoading }] = useMutation<
    RegisterPhoneCourseResponse,
    RegisterPhoneCourseVariables
  >(REGISTER_PHONE_COURSE)

  const [incrementRegisterCourseCount, { loading: countLoading }] = useMutation<
    IncrementOrderRegisterCourseCountResponse,
    IncrementOrderRegisterCourseCountVariables
  >(INCREMENT_ORDER_REGISTER_COURSE_COUNT)

  const loadings =
    activeCourse.isLoadingCourse ||
    registerLoading ||
    registerPhoneLoading ||
    countLoading

  const loadingDelay = useLoadingDelay(loadings)

  useEffect(() => {
    let title = ''
    if (activeCourse?.activeTab) {
      title =
        activeCourse.activeTab === 'first'
          ? CourseTabTitle.TAKE_FIRST_COURSE
          : CourseTabTitle.TAKE_SECOND_COURSE
    }

    dispatch(setTitle(title))
  }, [activeCourse?.activeTab])

  useEffect(() => {
    if (discInstUrlInfo?.courseOrderItemId) {
      handleRegisterCourse()
    }

    return () => {
      dispatch(setErrorMessage(null))
    }
  }, [discInstUrlInfo?.courseOrderItemId])

  const fields = useMemo<InfoField[]>(
    () => [
      {
        content: `Pending: ${getCourseStatusName(course.courseStatus)}`,
        key: 'courseStatus',
        title: 'Course Status'
      },
      {
        content: course.providerName,
        key: 'courseProvider',
        title: 'Course Provider'
      }
    ],
    [course.courseStatus, course.providerName]
  )

  async function registerCourseHelper(): Promise<boolean> {
    let result = false
    let registerCourseOrderItemId = 0

    if (discInstUrlInfo) {
      registerCourseOrderItemId = discInstUrlInfo.courseOrderItemId
    }

    try {
      const input = {
        caseId:
          activeCase.matter && activeCase.matter.id ? activeCase?.matter.id : 0,
        registerCourseOrderItemId: registerCourseOrderItemId
      }

      const incrementInput = {
        caseId:
          activeCase.matter && activeCase.matter.id ? activeCase?.matter.id : 0,
        courseType: course.courseType
      }

      // always want to bump the register count even if the register course fails
      await incrementRegisterCourseCount({
        variables: { request: incrementInput }
      })

      const updateResult = await registerCourse({
        variables: { request: input }
      })
      if (updateResult.data?.orders.registerCourse.success) {
        result = true
        const newCourseStatus = updateResult.data?.orders.registerCourse
          .courseStatus
          ? updateResult.data?.orders.registerCourse.courseStatus
          : 0
        const info1 = updateResult.data?.orders.registerCourse.info1
          ? updateResult.data?.orders.registerCourse.info1
          : 'noinfo1'
        const info2 = updateResult.data?.orders.registerCourse.info2
          ? updateResult.data?.orders.registerCourse.info2
          : 'noinfo2'
        const courseUrl = `${BACKEND_URL}/orders/courses/Provider${course.providerId}?info1=${info1}&info2=${info2}`
        const updateInfo: UpdateCourseDiscInstUrlInfoWithRegistration = {
          courseNumber: course.courseNumber,
          courseUrl: courseUrl,
          info1: updateResult.data?.orders.registerCourse.info1,
          info2: updateResult.data?.orders.registerCourse.info2,
          courseStatus: newCourseStatus
        }
        dispatch(setCourseDiscInstUrlInfoFields(updateInfo))
        if (course.courseMethod === CourseMethodType.Online) {
          // open new tab with the discInstUrlInfo.courseUrl

          if (courseUrl) {
            window.open(courseUrl, '_blank')
          }
        }
      } else {
        dispatch(setErrorMessage('Error Registering Course'))
      }
    } catch (e) {
      dispatch(setErrorMessage('Error Registering Course'))
      result = false
    }

    dispatch(setCourseLoading(false))
    return result
  }

  async function registerPhoneCourseHelper(): Promise<boolean> {
    let result = false
    let registerCourseOrderItemId = 0

    if (discInstUrlInfo) {
      registerCourseOrderItemId = discInstUrlInfo.courseOrderItemId
    }

    try {
      const input = {
        caseId:
          activeCase.matter && activeCase.matter.id ? activeCase?.matter.id : 0,
        registerCourseOrderItemId: registerCourseOrderItemId
      }

      const incrementInput = {
        caseId:
          activeCase.matter && activeCase.matter.id ? activeCase?.matter.id : 0,
        courseType: course.courseType
      }

      // always want to bump the register count even if the register course fails
      await incrementRegisterCourseCount({
        variables: { request: incrementInput }
      })

      const updateResult = await registerPhoneCourse({
        variables: { request: input }
      })
      if (updateResult.data?.orders.registerPhoneCourse.success) {
        result = true
        const newCourseStatus = updateResult.data?.orders.registerPhoneCourse
          .courseStatus
          ? updateResult.data?.orders.registerPhoneCourse.courseStatus
          : 0

        // really only need to update course status here. Using setCourseDiscInstUrlInfoFields since that method already exists.
        const updateInfo: UpdateCourseDiscInstUrlInfoWithRegistration = {
          courseNumber: course.courseNumber,
          courseUrl: '',
          info1: null,
          info2: null,
          courseStatus: newCourseStatus
        }
        dispatch(setCourseDiscInstUrlInfoFields(updateInfo))
      }

      dispatch(setCourseLoading(false))
      return result
    } catch (e) {
      dispatch(setErrorMessage('Error Registering Course'))
      result = false
    }
    return result
  }

  async function handleStartingCourse() {
    dispatch(setErrorMessage(null))

    let path: string | null = null
    if ((path = shouldNavigateToVerifyPrimaryInformation(course))) {
      history.push(path)
    } else if (
      (path = shouldNavigateToVerifySpouseInformation(
        isDebtor1,
        course,
        activeCase,
        activeCourse
      ))
    ) {
      history.push(path)
    } else if ((path = shouldNavigateToDisclosureAgreement(course))) {
      history.push(path)
    } else if ((path = shouldNavigateToCourseInstructions(course))) {
      history.push(path)
    } else {
      if (course.courseMethod === CourseMethodType.Online) {
        dispatch(setCourseLoading(true))
        await registerCourseHelper()
      } else if (course.courseMethod === CourseMethodType.Phone) {
        dispatch(setCourseLoading(true))
        await registerPhoneCourseHelper()
      }
    }
  }

  async function handleRegisterCourse() {
    // open online course in new tab if course is online; otherwise tell user to start phone call course
    if (
      course.courseMethod === CourseMethodType.Online &&
      course.courseStatus === CourseStatus.PendingRegistration &&
      course.verifiedDetails &&
      course.acceptedDisclosureAgreement &&
      course.shownCourseInstructions &&
      !course.hasRegisteredCourse
    ) {
      dispatch(setCourseLoading(true))
      await registerCourseHelper()
    } else if (
      course.courseMethod === CourseMethodType.Phone &&
      course.courseStatus === CourseStatus.PendingRegistrationPhone &&
      course.verifiedDetails &&
      course.acceptedDisclosureAgreement &&
      course.shownCourseInstructions &&
      !course.hasRegisteredCourse
    ) {
      dispatch(setCourseLoading(true))
      await registerPhoneCourseHelper()
    }
  }

  return (
    <CoursesContainer data-testid='course-tab-start-content'>
      {loadingDelay && loadingSpinnerPortal()}
      <StartBtn
        onClick={handleStartingCourse}
        data-testid='course-tab-start-button'
      >
        {currentTab == 'first'
          ? 'Start the First Course'
          : 'Start the Second Course'}
      </StartBtn>
      <InfoFields fields={fields} />
    </CoursesContainer>
  )
}

export default CourseTabStart
